import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Table from '../Components/Tables';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';
import Button from '../Components/Buttons';

function Organizations({ canEdit }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();

    const { getOrganizations } = useOrganizationApi();

    const [alertShown, setAlertShown] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [organizations, setOrganizations] = React.useState([]);
    const [totalOrganizations, setTotalOrganizations] = React.useState(0);
    const [itemsPerPage, setItemsPerPage] = React.useState(10);

    const columns = [
        { name: 'Title', selector: (row) => row.title, sortable: false },
        { name: 'Status', selector: (row) => row.is_active === true ? "Active" : "InActive", sortable: false },
    ];

    React.useEffect(() => {
        if (location.state?.successMessage && !alertShown) {
            showAlert('success', location.state.successMessage);
            setAlertShown(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

    const handleRowClicked = (row) => {
        navigate(`/organizations/${row.id}`);
    };

    const fetchOrganizations = async (skip, limit) => {
        if (limit !== itemsPerPage) {
            setItemsPerPage(limit);
        }
        try {
            setLoading(true);
            const response = await getOrganizations(skip, limit);
            setOrganizations(response.items);
            setTotalOrganizations(response.totalCount);
        } catch (error) {
            setError(error.message || 'Failed to fetch organizations!');
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        fetchOrganizations(0, itemsPerPage);
    }, []);

    return (
        <div className="container">
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-12">
                    {error ? (
                        <div>Error: {error}</div>
                    ) : (
                        <>
                            <Table
                                title="Organizations"
                                columns={columns}
                                data={organizations}
                                selectableRows
                                selectableRowsHighlight
                                progressPending={loading}
                                paginationPerPage={itemsPerPage}
                                paginationTotalRows={totalOrganizations}
                                dense={false}
                                noTableHead={false}
                                disabled={false}
                                onRowClicked={handleRowClicked}
                                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                                rowKeyField="key"
                                onChangePage={fetchOrganizations}
                                getRowUrl={(row) => `/organizations/${row.id}`}
                                SearchField
                            />
                            {canEdit && (
                                <Button
                                    background="color2"
                                    size="large"
                                    onClick={() => navigate('/new-organization')}
                                >
                                    Create New Organization
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function OrganizationsWrapper({ canEdit }) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <Organizations canEdit={canEdit} />
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default OrganizationsWrapper;